<template>
  <div
    :key="filterKey"
    ref="selectRef"
    class="select pop select--filter"
    :class="computedClasses"
  >
    <button
      ref="buttonRef"
      type="button" 
      class="button select__trigger"
      :aria-expanded="isOpen ? true : false"
      :aria-label="ariaLabel"
      :disabled="props.disabled"
      @click="toggleDropdownHandler"
    >
      <div class="select__labels">
        <p class="label m-b--0 text-KQ-Grey1">
          {{ selectionArray?.indexOf('all') !== -1 ? 'All' : '' }}
          {{
            selectionArray?.indexOf('all') !== -1
              ? name
              : name.charAt(0).toUpperCase() + name.slice(1)
          }}
        </p>
        <p
          v-if="selectionArray?.indexOf('all') === -1"
          class="select__labels-selected fs-12 text-KQ-Grey2"
        >
          {{ selectionArray?.length }} Selected
        </p>
      </div>
      <i
        v-if="!isInBottomSheet"
        class="icon icon-triangle"
        aria-hidden="true"
      ></i>
      <i
        v-else
        class="icon icon-chevrondown icon-color--red icon--xs"
        aria-hidden="true"
      ></i>
    </button>
    <p v-if="error.length" class="select__error">{{ error }}</p>
    <div ref="dropdownRef" class="select__dropdown">
      <button
        @click="selectAllHandler"
        class="select__dropdown-selectall"
        :class="{'selected': selectionArray && selectionArray.indexOf('all') !== -1}"
      >
        All {{ name }}
      </button>
      <ul ref="listRef" class="ul--cta ul--cta-lg">
        <slot
          name="options"
          :selectionHandler="selectionHandler"
          :selectionArray="selectionArray"
        />
      </ul>
      <div class="select__footer">
        <button
          ref="cancelButtonRef"
          role="button"
          class="button button--text"
          aria-disabled="true"
          @click="resetHandler"
        >
          Cancel
        </button>
        <button
          ref="updateButtonRef"
          role="button"
          class="button button--primary"
          :disabled="!stateChanged || selectionArray.length === 0"
          @click="updateHandler"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeMount, onMounted, ref, watch } from 'vue';
const isInBottomSheet = ref(false);

const randomKeyHandler = () => {
  return Math.random().toString(36).substring(2, 12);
};

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  classes: {
    type: String,
    default: '',
  },
  injectionName: {
    type: String,
    default: 'injectedState',
  },
  callBack: {
    type: Function,
    default: () => {},
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:value']);

const isOpen = ref(false);
const eventsSet = ref(false);
const listRef = ref();
const selectRef = ref();
const buttonRef = ref();
const dropdownRef = ref();
const stateChanged = ref(false);
const selectionArray = ref(['all']);
const oldSelectionArray = ref(['all']);
const updateButtonRef = ref();
const cancelButtonRef = ref();
const injectedState = inject(props.injectionName);
const filterKey = 'filter' + randomKeyHandler();

const computedClasses = computed(() => {
  return {
    hasError: props.error.length > 0,
    isOpen: isOpen.value === true,
    [`${props.classes}`]: true,
  };
});

const toggleDropdownHandler = () => {
  if (isOpen.value === false) {
    oldSelectionArray.value = [...selectionArray.value];
  }
  isOpen.value = !isOpen.value;
  buttonRef.value.focus();
};

const resetHandler = () => {
  selectionArray.value = [...oldSelectionArray.value];
  toggleDropdownHandler();
};

const updateHandler = () => {
  oldSelectionArray.value = [...selectionArray.value];
  emit('update:value', {
    value: [...selectionArray.value],
    name: props.name,
  });
  props.callBack({
    value: [...selectionArray.value],
    name: props.name,
  });
  toggleDropdownHandler();
};

const selectAllHandler = (e) => {
  selectionArray.value = ['all'];
  if (selectRef?.value?.closest('.bottom-sheet') !== null) {
    emit('update:value', {
      value: [...selectionArray.value],
      name: props.name,
    });
  }
};

const selectionHandler = (e) => {
  stateChanged.value = true;
  if (e.target.checked) {
    const valueIndex = selectionArray.value.indexOf('all');
    if (valueIndex !== -1) {
      selectionArray.value.splice(valueIndex, 1);
    }
    selectionArray.value.push(e.target.value);
  } else {
    const valueIndex = selectionArray.value.indexOf(e.target.value);
    if (valueIndex !== -1) {
      selectionArray.value.splice(valueIndex, 1);
    }
    if (selectionArray.value.length === 0) {
      selectionArray.value.push('all');
    }
  }

  if (selectRef?.value?.closest('.bottom-sheet') !== null) {
    emit('update:value', {
      value: [...selectionArray.value],
      name: props.name,
    });
  }
};

const collapseHandler = (e) => {
  const target = e?.target;
  if (selectRef.value?.contains(target)) {
    return;
  }
  resetHandler();
};

const tabHandler = (e) => {
  if (e.which === 9 && !e.shiftKey) {
    const isCancelButton = e.target.innerHTML === 'Cancel';
    if (isCancelButton) {
      if (stateChanged.value === false || selectionArray.value.length === 0) {
        e.preventDefault();
        buttonRef.value.focus({ focusVisible: true });
      }
    } else {
      buttonRef.value.focus({ focusVisible: true });
    }
  }
};

const checkOverflow = () => {
  if (isInBottomSheet.value === true) {
    dropdownRef.value.style.opacity = 1;
    return;
  }
  dropdownRef.value.style.opacity = 0;

  const { height, top } = dropdownRef.value.getBoundingClientRect();
  if (height === 0) {
    return;
  }
  const smallestValue = Math.min(height, 560);
  dropdownRef.value.style.height = smallestValue + 'px';
  dropdownRef.value.style.opacity = 1;
};

const handleResize = () => {
  checkOverflow();
};

const windowEventsHandler = (bool) => {
  if (bool === eventsSet.value) {
    return;
  }
  eventsSet.value = bool;

  if (bool) {
    window.addEventListener('click', collapseHandler);
    window.addEventListener('scroll', collapseHandler);
    window.addEventListener('resize', handleResize);
    updateButtonRef.value.addEventListener('keydown', tabHandler);
    cancelButtonRef.value.addEventListener('keydown', tabHandler);
  } else {
    window.removeEventListener('click', collapseHandler);
    window.removeEventListener('scroll', collapseHandler);
    window.removeEventListener('resize', handleResize);
    updateButtonRef.value.removeEventListener('keydown', tabHandler);
    cancelButtonRef.value.removeEventListener('keydown', tabHandler);
  }
};

onMounted(() => {
  isInBottomSheet.value = selectRef?.value?.closest('.bottom-sheet') !== null;
});

watch(isOpen, () => {
  windowEventsHandler(isOpen.value);
  setTimeout(() => {
    handleResize();
  }, 200);
});

watch(
  () => injectedState?.value,
  () => {
    if (injectedState?.value[props.name].length === 0) {
      selectionArray.value = ['all'];
    } else {
      selectionArray.value = injectedState?.value?.[props.name]
        ? [...injectedState.value[props.name]]
        : ['all'];
    }
  },
);

onBeforeMount(() => {
  if (injectedState?.value?.[props.name].length > 0) {
    selectionArray.value = [...injectedState?.value?.[props.name]]
  }
})
</script>
